import Dispatcher from '../dispatcher/CentricDispatcher.react';
import { ActionTypes, StatTypes, SearchTypes } from '../constants/CentricConstants';
import UserApi from '../server/UserApi';
import SettingStore from '../stores/SettingStore';

function createSuccessHandler(actions, payload) {
    if (!Array.isArray(actions)) { actions = [actions]; }

    return function(response) {
        actions.forEach(function(action) {
            Dispatcher.dispatch({
                type: action,
                data: response.data,
                payload: payload || null
            });
        });
    };
}

function createErrorHandler() {
    let actions = Array.from(arguments);
    return function(response) {
        actions.forEach(function(action) {
            Dispatcher.dispatch({
                type: action,
                data: response.message
            });
        });
    };
}


const _handleFormError = createErrorHandler(ActionTypes.FORM_SUBMIT_ERROR);
const _handleException = createErrorHandler(ActionTypes.API_EXCEPTION);

export default {

    getAll: function(params) {
        const handleSuccess = createSuccessHandler(ActionTypes.USER_LIST);
        const handleError = createErrorHandler(ActionTypes.USER_LIST_ERROR);

        return UserApi.getAll(params)
            .then(handleSuccess, handleError)
            .catch(handleError);
    },

    getById: function(id) {
        const handleSuccess = createSuccessHandler(ActionTypes.USER_INFO, {id: id});
        const handleError = createErrorHandler(ActionTypes.USER_INFO_ERROR);

        return UserApi.getById(id)
            .then(handleSuccess, handleError)
            .catch(handleError);
    },

    save: function(entity) {
        const handleSuccess = createSuccessHandler([ActionTypes.USER_SAVE, ActionTypes.FORM_SUBMIT_SUCCESS], {entity: entity});
        const handleError = createErrorHandler(ActionTypes.USER_INFO_ERROR);

        return UserApi.save(entity)
            .then(handleSuccess, _handleFormError)
            .catch(handleError);
    },

    update: function(id, entity) {
        const handleSuccess = createSuccessHandler([ActionTypes.USER_UPDATE, ActionTypes.FORM_SUBMIT_SUCCESS], {id: id, entity: entity});
        const handleError = createErrorHandler(ActionTypes.USER_UPDATE_ERROR);

        return UserApi.update(id, entity)
            .then(handleSuccess, _handleFormError)
            .catch(handleError);
    },

    delete: function(id) {
        const types = {
            success: ActionTypes.USER_DELETE,
            failure: [ActionTypes.USER_DELETE_ERROR, ActionTypes.FORM_SUBMIT_ERROR]
        };

        return Dispatcher.dispatchAsync(UserApi.delete(id), types, {id, showDeleted: !!SettingStore.getValue('SHOW_DELETED_USERS')});
    },

    restoreDeletedUser: (id, params) => {
        const types = {
            request: ActionTypes.USER_RESTORE_DELETED_REQUEST,
            success: ActionTypes.USER_RESTORE_DELETED_SUCCESS,
            failure: ActionTypes.USER_RESTORE_DELETED_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.restoreDeleted(id, params), types, {id, params});
    },

    restoreArchivedUser: (id) => {
        const types = {
            success: ActionTypes.USER_UPDATE,
            failure: ActionTypes.USER_UPDATE_ERROR
        };

        return Dispatcher.dispatchAsync(UserApi.update(id, {archived: false}), types, {id});
    },

    search: (searchText, params, key = SearchTypes.USERS) => {
        const types = {
            request:    ActionTypes.SEARCH_REQUEST,
            success:    [ActionTypes.SEARCH_SUCCESS, ActionTypes.USER_LIST],
            failure:    ActionTypes.SEARCH_FAILURE
        };
        // USER_LIST: CourseEnrolments.react.js

        return Dispatcher.dispatchAsync(UserApi.search(searchText, params), types, {searchText, params, key});
    },

    searchTrainers: function(searchText) {
        const handleSuccess = createSuccessHandler(ActionTypes.SEARCH_RESULT_RECEIVED);
        const handleError = createErrorHandler(ActionTypes.SEARCH_ERROR);

        return UserApi.searchTrainers(searchText)
            .then(handleSuccess, handleError)
            .catch(handleError);
    },

    passwordReset: function(params) {
        const types = {
            success:    [ActionTypes.USER_PASSWORD_RESET, ActionTypes.FORM_SUBMIT_SUCCESS],
            failure:    [ActionTypes.USER_PASSWORD_RESET_ERROR, ActionTypes.FORM_SUBMIT_ERROR]
        };

        return Dispatcher.dispatchAsync(UserApi.passwordReset(params), types, {params});
    },

    changePassword: (params) => {
        const types = {
            success:    [ActionTypes.USER_CHANGE_PASSWORD_SUCCESS, ActionTypes.FORM_SUBMIT_SUCCESS],
            failure:    [ActionTypes.USER_CHANGE_PASSWORD_FAILURE, ActionTypes.FORM_SUBMIT_ERROR]
        };

        return Dispatcher.dispatchAsync(UserApi.changePassword(params), types, {params});
    },

    sendWelcomeEmail: (userId) => {
        const types = {
            request: ActionTypes.USER_WELCOME_EMAIL_REQUEST,
            success: [ActionTypes.USER_WELCOME_EMAIL_SUCCESS, ActionTypes.FORM_SUBMIT_SUCCESS],
            failure: ActionTypes.USER_WELCOME_EMAIL_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.sendWelcomeEmail(userId), types, {userId});
    },

    sendMissingWelcomeEmails: function(params) {
        const handleSuccess = createSuccessHandler(ActionTypes.MISSING_WELCOME_EMAILS);
        const handleError = createErrorHandler(ActionTypes.MISSING_WELCOME_EMAILS_ERROR);

        return UserApi.sendMissingWelcomeEmails(params)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },

    syncWithIAM: () => {
        const types = {
            request: ActionTypes.USER_IAM_SYNC_REQUEST,
            success: ActionTypes.USER_IAM_SYNC_SUCCESS,
            failure: ActionTypes.USER_IAM_SYNC_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.syncWithIAM(), types);
    },

    getCount: function() {
        const types = {
            request:    ActionTypes.STATS_INFO_REQUEST,
            success:    ActionTypes.STATS_INFO_SUCCESS,
            failure:    ActionTypes.STATS_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.getCount(), types, { key: StatTypes.USER_COUNT } );
    },

    getArchivedCount: function() {
        const types = {
            request:    ActionTypes.STATS_INFO_REQUEST,
            success:    ActionTypes.STATS_INFO_SUCCESS,
            failure:    ActionTypes.STATS_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.getArchivedCount(), types, { key: StatTypes.USER_COUNT_ARCHIVED } );
    },

    getVisitStats: () => {
        const types = {
            request:    ActionTypes.STATS_INFO_REQUEST,
            success:    ActionTypes.STATS_INFO_SUCCESS,
            failure:    ActionTypes.STATS_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.getVisitStats(), types, { key: StatTypes.USER_VISIT_STATS } );
    },

    validateUsername: (username, firstName = null, lastName = null) => {
        const types = {
            request: ActionTypes.USER_USERNAME_VALIDATE_REQUEST,
            success: ActionTypes.USER_USERNAME_VALIDATE_SUCCESS,
            failure: ActionTypes.USER_USERNAME_VALIDATE_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.validateUsername(username, firstName, lastName), types, {username, firstName, lastName});
    },

    getTrainers: function(params) {
        const handleSuccess = createSuccessHandler(ActionTypes.TRAINER_LIST);
        const handleError = createErrorHandler(ActionTypes.TRAINER_LIST_ERROR);

        return UserApi.getTrainers(params)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },

    getManagers: (params) => {
        const types = {
            request:    ActionTypes.MANAGER_LIST_REQUEST,
            success:    ActionTypes.MANAGER_LIST_SUCCESS,
            failure:    ActionTypes.MANAGER_LIST_FAILURE
        };
        return Dispatcher.dispatchAsync(UserApi.getManagers(params), types, {params});
    },

    addManager: function(id, params) {
        const handleSuccess = createSuccessHandler(ActionTypes.USER_MANAGER_SAVE);
        const handleError = createErrorHandler(ActionTypes.USER_MANAGER_SAVE_ERROR);

        return UserApi.addManager(id, params)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },

    removeManager: function(id, managerId) {
        const handleSuccess = createSuccessHandler(ActionTypes.USER_MANAGER_REMOVE);
        const handleError = createErrorHandler(ActionTypes.USER_MANAGER_REMOVE_ERROR);

        return UserApi.removeManager(id, managerId)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },

    addDepartment: function(id, params) {
        const handleSuccess = createSuccessHandler(ActionTypes.USER_DEPARTMENT_SAVE);
        const handleError = createErrorHandler(ActionTypes.USER_DEPARTMENT_SAVE_ERROR);

        return UserApi.addDepartment(id, params)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },

    removeDepartment: (id, departmentId) => {
        const types = {
            request:    ActionTypes.USER_DEPARTMENT_REMOVE_REQUEST,
            success:    ActionTypes.USER_DEPARTMENT_REMOVE_SUCCESS,
            failure:    ActionTypes.USER_DEPARTMENT_REMOVE_FAILURE,
        };

        return Dispatcher.dispatchAsync(UserApi.removeDepartment(id, departmentId), types, {id, departmentId});
    },

    addGroup: function(id, params) {
        const handleSuccess = createSuccessHandler(ActionTypes.USER_GROUP_SAVE);
        const handleError = createErrorHandler(ActionTypes.USER_GROUP_SAVE_ERROR);

        return UserApi.addGroup(id, params)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },

    removeGroup: function(userId, groupId) {
        const types = {
            request:    ActionTypes.USER_GROUP_REMOVE_REQUEST,
            success:    ActionTypes.USER_GROUP_REMOVE_SUCCESS,
            failure:    ActionTypes.USER_GROUP_REMOVE_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.removeGroup(userId, groupId), types, {userId, groupId});
    },

    getAttributes: () => {
        const types = {
            request:    ActionTypes.ATTRIBUTE_LIST_REQUEST,
            success:    ActionTypes.ATTRIBUTE_LIST_SUCCESS,
            failure:    ActionTypes.ATTRIBUTE_LIST_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.getAttributes(), types);
    },

    getActiveAttributes: () => {
        const types = {
            request:    ActionTypes.ATTRIBUTE_ACTIVE_LIST_REQUEST,
            success:    ActionTypes.ATTRIBUTE_ACTIVE_LIST_SUCCESS,
            failure:    ActionTypes.ATTRIBUTE_ACTIVE_LIST_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.getActiveAttributes(), types);
    },

    getUserEditableAttributes: () => {
        const types = {
            request:    ActionTypes.ATTRIBUTE_LIST_REQUEST,
            success:    ActionTypes.ATTRIBUTE_LIST_SUCCESS,
            failure:    ActionTypes.ATTRIBUTE_LIST_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.getUserEditableAttributes(), types);
    },

    setAttributeActive: (code, value) => {
        const types = {
            request: ActionTypes.ATTRIBUTE_SET_ACTIVE_REQUEST,
            success: ActionTypes.ATTRIBUTE_SET_ACTIVE_SUCCESS,
            failure: ActionTypes.ATTRIBUTE_SET_ACTIVE_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.setAttributeActive(code, value), types, {code, value});
    },

    setAttributeRequired: (code, value) => {
        const types = {
            request: ActionTypes.ATTRIBUTE_SET_REQUIRED_REQUEST,
            success: ActionTypes.ATTRIBUTE_SET_REQUIRED_SUCCESS,
            failure: ActionTypes.ATTRIBUTE_SET_REQUIRED_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.setAttributeRequired(code, value), types, {code, value});
    },

    setAttributeUserEditable: (code, value) => {
        const types = {
            request: ActionTypes.ATTRIBUTE_SET_USER_EDITABLE_REQUEST,
            success: ActionTypes.ATTRIBUTE_SET_USER_EDITABLE_SUCCESS,
            failure: ActionTypes.ATTRIBUTE_SET_USER_EDITABLE_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.setAttributeUserEditable(code, value), types, {code, value});
    },

    setAttributeRegistrationField: (code, value) => {
        const types = {
            request: ActionTypes.ATTRIBUTE_SET_REGISTRATION_FIELD_REQUEST,
            success: ActionTypes.ATTRIBUTE_SET_REGISTRATION_FIELD_SUCCESS,
            failure: ActionTypes.ATTRIBUTE_SET_REGISTRATION_FIELD_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.setAttributeRegistrationField(code, value), types, {code, value});
    },

    getUserSkill: (id, skillId) => {
        const types = {
            request:ActionTypes.USER_SKILL_INFO_REQUEST,
            success:ActionTypes.USER_SKILL_INFO_SUCCESS,
            failure:ActionTypes.USER_SKILL_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(UserApi.getUserSkill(id, skillId), types, {userId: id, skillId});
    },

    getSkillsByUser: (id, params) => {
        const types = {
            success: ActionTypes.USER_SKILL_LIST,
            failure: ActionTypes.USER_SKILL_LIST_ERROR
        };
        return Dispatcher.dispatchAsync(UserApi.getSkillsByUser(id, params), types, {userId: id, params});
    },

    searchSkillsByUser: (id, searchText, params) => {
        const types = {
            request:    ActionTypes.SEARCH_REQUEST,
            success:    [ActionTypes.SEARCH_SUCCESS, ActionTypes.USER_SKILL_LIST],
            failure:    ActionTypes.SEARCH_FAILURE
        };
        return Dispatcher.dispatchAsync(UserApi.searchSkillsByUser(id, searchText, params), types, {id, searchText, params});
    },

    addSkill: function(id, skillId, params) {
        const handleSuccess = createSuccessHandler(ActionTypes.USER_SKILL_SAVE, {userId: id});
        const handleError = createErrorHandler(ActionTypes.USER_SKILL_SAVE_ERROR);

        return UserApi.addSkill(id, skillId, params)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },

    updateSkill: (id, skillId, params) => {
        const types = {
            request: ActionTypes.USER_SKILL_UPDATE_REQUEST,
            success: ActionTypes.USER_SKILL_UPDATE_SUCCESS,
            failure: ActionTypes.USER_SKILL_UPDATE_FAILURE
        };
        return Dispatcher.dispatchAsync(UserApi.updateSkill(id, skillId, params), types, {userId: id, skillId, params});
    },

    removeSkill: (id, skillId) => {
        const types = {
            request: ActionTypes.USER_SKILL_REMOVE_REQUEST,
            success: ActionTypes.USER_SKILL_REMOVE_SUCCESS,
            failure: ActionTypes.USER_SKILL_REMOVE_FAILURE
        };
        return Dispatcher.dispatchAsync(UserApi.removeSkill(id, skillId), types, {userId: id, skillId});
    },

    listAuthMethods: () => {
        const types = {
            request:    ActionTypes.USER_AUTH_METHOD_LIST_REQUEST,
            success:    ActionTypes.USER_AUTH_METHOD_LIST_SUCCESS,
            failure:    ActionTypes.USER_AUTH_METHOD_LIST_FAILURE
        };
        return Dispatcher.dispatchAsync(UserApi.listAuthMethods(), types);
    },

    importUsers: (payload) => {
        const types = {
            request: ActionTypes.USER_IMPORT_REQUEST,
            success: [ActionTypes.FORM_SUBMIT_SUCCESS, ActionTypes.USER_IMPORT_SUCCESS],
            failure: [ActionTypes.FORM_SUBMIT_ERROR, ActionTypes.USER_IMPORT_FAILURE],
        };
        return Dispatcher.dispatchAsync(UserApi.importUsers(payload), types, payload);
    },

    getImportHeaders: () => {
        const types = {
            request: ActionTypes.USER_IMPORT_HEADERS_REQUEST,
            success: ActionTypes.USER_IMPORT_HEADERS_SUCCESS,
            failure: ActionTypes.USER_IMPORT_HEADERS_FAILURE
        };
        return Dispatcher.dispatchAsync(UserApi.getImportHeaders(), types);
    },

    emitError: (data) => {
        if (!Dispatcher._isDispatching) {
            return Dispatcher.dispatch({type: ActionTypes.FORM_SUBMIT_ERROR, data});
        }
    },
    setTargetFile: (payload) => {
        if (!Dispatcher._isDispatching) {
            return Dispatcher.dispatch({type: ActionTypes.USER_IMPORT_SET_TARGET_FILE, payload});
        }
    },
    setProcessed: (payload) => {
        if (!Dispatcher._isDispatching) {
            return Dispatcher.dispatch({type: ActionTypes.USER_IMPORT_SET_PROCESSED, payload});
        }
    }
};

export const listUserCompliances = (userId) => {
    const types = {
        request:    ActionTypes.USER_COMPLIANCE_LIST_REQUEST,
        success:    ActionTypes.USER_COMPLIANCE_LIST_SUCCESS,
        failure:    ActionTypes.USER_COMPLIANCE_LIST_FAILURE
    };
    return Dispatcher.dispatchAsync(UserApi.listCompliances(userId), types, {userId});
};

export const addUserCompliance = (userId, complianceId) => {
    const types = {
        request:    ActionTypes.USER_COMPLIANCE_ADD_REQUEST,
        success:    ActionTypes.USER_COMPLIANCE_ADD_SUCCESS,
        failure:    ActionTypes.USER_COMPLIANCE_ADD_FAILURE
    };

    return Dispatcher.dispatchAsync(UserApi.addCompliance(userId, complianceId), types, {userId, complianceId});
};

export const removeUserCompliance = (userId, complianceId) => {
    const types = {
        request:    ActionTypes.USER_COMPLIANCE_REMOVE_REQUEST,
        success:    ActionTypes.USER_COMPLIANCE_REMOVE_SUCCESS,
        failure:    ActionTypes.USER_COMPLIANCE_REMOVE_FAILURE
    };

    return Dispatcher.dispatchAsync(UserApi.removeCompliance(userId, complianceId), types, {userId, complianceId});
};

export const getAuthTokenCount = () => {
    const types = {
        request:    ActionTypes.STATS_INFO_REQUEST,
        success:    ActionTypes.STATS_INFO_SUCCESS,
        failure:    ActionTypes.STATS_INFO_FAILURE
    };
    return Dispatcher.dispatchAsync(UserApi.getAuthTokenCount(), types, { key: StatTypes.AUTH_TOKEN_COUNT } );
};

export const clearAuthTokens = () => {
   const types = {
       request: ActionTypes.USER_TOKENS_CLEAR_REQUEST,
       success: [ActionTypes.USER_TOKENS_CLEAR_SUCCESS, ActionTypes.FORM_SUBMIT_SUCCESS],
       failure: ActionTypes.USER_TOKENS_CLEAR_FAILURE
   };
   return Dispatcher.dispatchAsync(UserApi.clearAuthTokens(), types);
};

